import React, { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/use-toast";
import { UserPlus, ArrowLeft } from "lucide-react";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { useUsageLimits } from "@/hooks/useBillingAndUsage";
import { limitExceededMessage, toFriendlyLimitError } from "@/lib/limitErrors";
import { useOrganization } from "@/contexts/OrganizationContext";
import {
  formatInvalidInfluencerHandlesMessage,
  validateInfluencerHandles,
  type ValidatedInfluencerProfile,
} from "@/lib/influencerValidation";
import { cleanHandle, cleanHandles } from "@/lib/utils/csvParsing";
import {
  type InfluencerFormData,
  type AddInfluencerModalProps,
  INITIAL_INFLUENCER_STATE,
  parseFollowersCount,
  linkInfluencersToCampaign,
  ManualHandleInput,
  CsvImportPromo,
} from "./influencers/add-modal";

export type { AddInfluencerModalProps, InfluencerFormData as Influencer };

type InfluencerInsert = Database["public"]["Tables"]["influencers"]["Insert"];
type Platform = Database["public"]["Enums"]["platform_enum"];
type Gender = Database["public"]["Enums"]["gender_enum"];

export const AddInfluencerModal: React.FC<AddInfluencerModalProps> = ({
  open,
  onOpenChange,
  onSave,
  existingInfluencers,
  onImportClick,
  onBack,
  campaignId,
}) => {
  const [step, setStep] = useState(1);
  const [values, setValues] = useState<InfluencerFormData>({
    ...INITIAL_INFLUENCER_STATE,
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ [k: string]: string }>({});
  const queryClient = useQueryClient();

  const { currentOrganization } = useOrganization();
  const { isAtLimit } = useUsageLimits();

  useEffect(() => {
    if (!open) {
      setStep(1);
      setValues({ ...INITIAL_INFLUENCER_STATE });
      setErrors({});
    }
  }, [open]);

  const validateStep = (): boolean => {
    const errs: { [k: string]: string } = {};
    if (step === 1) {
      if (!values.platform) errs.platform = "Select a platform.";
      if (!values.handle.trim()) {
        errs.handle = "At least one handle is required.";
      } else {
        const handles = cleanHandles(values.handle);
        if (handles.length === 0) errs.handle = "No valid handles found.";
      }
      if (
        values.email &&
        !/^[\w\-.]+@([\w-]+\.)+[\w-]{2,}$/.test(values.email)
      )
        errs.email = "Invalid email.";
      if (values.phone && !/^\d{6,13}$/.test(values.phone))
        errs.phone = "Invalid phone number.";
      if (
        values.followers &&
        parseFollowersCount(values.followers) === null
      ) {
        errs.followers =
          "Invalid followers format. Use numbers or K/M suffix (e.g., 2.5M, 250K)";
      }
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!validateStep()) return;

    if (!currentOrganization?.id) {
      toast({
        title: "Error",
        description:
          "No organization selected. Please select an organization first.",
        variant: "destructive",
      });
      return;
    }

    if (isAtLimit("influencers")) {
      toast({
        title: "Influencer limit reached",
        description: limitExceededMessage("influencers"),
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    const allHandles = cleanHandles(values.handle);

    if (allHandles.length === 0) {
      setLoading(false);
      setErrors({ handle: "No valid handles found." });
      return;
    }

    const { data: existingRows, error: existingError } = await supabase
      .from("influencers")
      .select("id, handle")
      .eq("organization_id", currentOrganization.id)
      .in("handle", allHandles);

    if (existingError) {
      setLoading(false);
      toast({
        title: "Failed to check influencers",
        description:
          existingError.message || "Could not verify existing handles.",
        variant: "destructive",
      });
      return;
    }

    const existingHandles = new Set<string>();
    const existingHandleToId = new Map<string, string>();
    (existingRows ?? []).forEach((influencer) => {
      const handle = cleanHandle(influencer.handle);
      existingHandles.add(handle);
      existingHandleToId.set(handle, influencer.id);
    });
    existingInfluencers
      .filter(
        (influencer) => influencer.organization_id === currentOrganization.id
      )
      .forEach((influencer) => {
        existingHandles.add(cleanHandle(influencer.handle ?? ""));
      });

    const newHandles = allHandles.filter(
      (handle) => !existingHandles.has(handle)
    );
    const skippedCount = allHandles.length - newHandles.length;

    // Existing influencers the user pasted that aren't yet part of this campaign.
    const existingIdsToLink = campaignId
      ? Array.from(existingHandleToId.values())
      : [];

    if (newHandles.length === 0) {
      setLoading(false);
      if (campaignId && existingIdsToLink.length > 0) {
        const linkedCount = await linkInfluencersToCampaign(
          campaignId,
          existingIdsToLink,
          queryClient
        );
        toast({
          title:
            linkedCount > 0
              ? "Influencers linked to campaign"
              : "No new influencers to link",
          description:
            linkedCount > 0
              ? `Linked ${linkedCount} existing influencer(s) to the campaign.`
              : "These influencers are already part of this campaign.",
          duration: 3500,
        });
        onSave({ ...INITIAL_INFLUENCER_STATE });
        onOpenChange(false);
        return;
      }
      setErrors({
        handle: "All entered handles already exist in your organization.",
      });
      toast({
        title: "No new influencers added",
        description: "All entered handles already exist in your organization.",
      });
      return;
    }

    let handles: string[] = [];
    let profileByHandle = new Map<string, ValidatedInfluencerProfile>();
    try {
      const validation = await validateInfluencerHandles(newHandles);
      handles = validation.validHandles;
      profileByHandle = new Map(
        validation.profiles.map((profile) => [profile.handle, profile])
      );

      if (validation.invalidHandles.length > 0) {
        const message = formatInvalidInfluencerHandlesMessage(
          validation.invalidHandles
        );
        setErrors({ handle: message });
        toast({
          title: "Influencer not found",
          description: message,
          variant: "destructive",
        });
      }
    } catch (error: unknown) {
      setLoading(false);
      const message =
        error instanceof Error
          ? error.message
          : "We couldn't verify the Instagram usernames right now. Please try again in a moment.";
      toast({
        title: "Couldn't verify influencers",
        description: message,
        variant: "destructive",
      });
      return;
    }

    if (handles.length === 0) {
      setLoading(false);
      return;
    }

    const platform = values.platform.toLowerCase() as Platform;
    const gender = values.gender ? (values.gender as Gender) : null;

    const influencersPayload: InfluencerInsert[] = handles.map((handle) => {
      const profile = profileByHandle.get(handle);

      return {
        name: profile?.name ?? handle,
        handle: handle,
        platform,
        organization_id: currentOrganization.id,
        created_at: new Date().toISOString(),
        enrichment_status: profile ? "completed" : "pending",
        last_refreshed_at: profile ? new Date().toISOString() : null,
        followers_count: profile?.followersCount ?? null,
        following_count: profile?.followingCount ?? null,
        profile_pic_url: profile?.profilePicUrl ?? null,
        bio: profile?.bio ?? null,
        email: values.email || profile?.email || null,
        business_category: profile?.businessCategory ?? null,
        categories: profile?.businessCategory
          ? [profile.businessCategory]
          : null,
        engagement_rate: profile?.engagementRate ?? null,
        engagement_rate_views: profile?.engagementRateViews ?? null,
        engagement_rate_followers: profile?.engagementRateFollowers ?? null,
        avg_likes: profile?.avgLikes ?? null,
        video_view_count: profile?.videoViewCount ?? null,
        verified: profile?.verified ?? null,
        instagram_pk: profile?.instagramPk ?? null,
        location: values.location || null,
        gender,
        agency_managed: !!values.agencyManaged,
      };
    });

    try {
      const { data, error } = await supabase
        .from("influencers")
        .insert(influencersPayload)
        .select();

      setLoading(false);

      if (error) {
        const isDuplicate =
          error.code === "23505" ||
          error.message?.includes("unique") ||
          error.message?.includes("duplicate");
        toast({
          title: isDuplicate
            ? "Duplicates detected"
            : "Failed to add influencers",
          description: isDuplicate
            ? "Some of these influencers have already been added to your organization."
            : toFriendlyLimitError(
                error,
                "We couldn't add the influencers right now. Please try again."
              ),
          variant: "destructive",
        });
        return;
      }

      if (data && data.length > 0) {
        await queryClient.invalidateQueries({
          queryKey: ["influencers", currentOrganization.id],
        });

        let linkedExistingCount = 0;
        if (campaignId) {
          const newIds = data.map((row) => row.id);
          linkedExistingCount = await linkInfluencersToCampaign(
            campaignId,
            [...newIds, ...existingIdsToLink],
            queryClient
          );
        }

        toast({
          title: "Influencers added",
          description: campaignId
            ? `Added ${data.length} new influencer(s) to the campaign${
                linkedExistingCount > 0
                  ? ` and linked ${linkedExistingCount} existing influencer(s)`
                  : ""
              }.`
            : skippedCount > 0
              ? `Added ${data.length} new influencer(s). Skipped ${skippedCount} already in your organization.`
              : `Successfully added ${data.length} influencer(s).`,
          duration: 3500,
        });
      }

      onSave({ ...INITIAL_INFLUENCER_STATE });
      onOpenChange(false);
    } catch (e: unknown) {
      setLoading(false);
      const message =
        e instanceof Error ? e.message : "Something went wrong.";
      toast({
        title: "Error",
        description: toFriendlyLimitError(e, message),
        variant: "destructive",
      });
    }
  };

  if (!currentOrganization) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-[500px] p-0 overflow-hidden font-hanken rounded-[16px] border border-slate-100">
          <DialogHeader className="p-6 border-b border-slate-100 flex flex-row items-center gap-4 text-left">
            <div className="w-12 h-12 rounded-full bg-[#FAF5FF] flex items-center justify-center text-[#A655F8] shrink-0">
              <UserPlus className="w-6 h-6 stroke-[1.8]" />
            </div>
            <div className="flex-grow">
              <DialogTitle className="text-[20px] font-bold text-slate-900 leading-tight">
                Add Influencer
              </DialogTitle>
            </div>
          </DialogHeader>
          <div className="p-8 text-center">
            <p className="text-slate-600 text-sm leading-relaxed">
              Please select an organization first to add influencers.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[600px] p-0 overflow-hidden font-hanken rounded-[16px] border border-slate-100/80 bg-white shadow-xl">
        <DialogHeader className="p-6 border-b border-slate-100 flex flex-row items-center gap-3.5 text-left">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="w-9 h-9 rounded-full border border-slate-200 hover:bg-slate-50 flex items-center justify-center text-slate-500 hover:text-slate-700 transition-colors focus:outline-none shrink-0"
              aria-label="Back"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
          )}
          <div className="w-12 h-12 rounded-full bg-[#A655F8]/10 flex items-center justify-center text-[#A655F8] shrink-0">
            <UserPlus className="w-6 h-6 stroke-[1.8]" />
          </div>
          <div className="flex-grow">
            <DialogTitle className="text-[20px] font-semibold text-slate-900 leading-tight">
              Add Influencer
            </DialogTitle>
            <DialogDescription className="text-[14px] text-slate-500 font-normal mt-1 leading-normal">
              Add one or more influencers to get started
            </DialogDescription>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="px-6 space-y-6">
          <div className="space-y-2">
            <ManualHandleInput
              value={values.handle}
              onChange={(val) =>
                setValues((v) => ({ ...v, handle: val }))
              }
              error={errors.handle}
            />

            <CsvImportPromo onImportClick={onImportClick} />
          </div>
        </form>

        <DialogFooter className="px-6 py-2 flex flex-row items-center justify-end gap-3 border-t border-slate-100 rounded-b-lg">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="h-10 px-5 rounded-[10px] text-[14px]"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={loading}
            className="h-10 px-5 text-[14px] rounded-[10px]"
          >
            {loading ? (
              <LoadingSpinner
                size="small"
                iconClassName="text-white"
                label="Adding..."
              />
            ) : (
              <span>Add Influencer</span>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddInfluencerModal;
